// src/components/tickets/MyTickets.js
import { useState, useEffect, useCallback } from 'react';
import TicketItem from './TicketItem';
import { syncXPFromTickets } from '../gamification/XPSystem';
import { getTicketsByOwner } from '../../services/subgraph';
import { eventTicketWithEvents } from '../../services/contracts';

// Cache key for localStorage
const CACHE_KEY_PREFIX = 'mytickets_cache_';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const STALE_WHILE_REVALIDATE = 30 * 60 * 1000; // 30 minutes - show stale data while refreshing

// Get cached tickets from localStorage
// Returns { tickets, isStale, age } or null
function getCachedTickets(account, allowStale = false) {
  if (!account) return null;

  try {
    const cacheKey = CACHE_KEY_PREFIX + account.toLowerCase();
    const cached = localStorage.getItem(cacheKey);

    if (!cached) return null;

    const { tickets, timestamp, contractAddress } = JSON.parse(cached);
    const age = Date.now() - timestamp;

    // Fresh cache - less than 5 minutes old
    if (age < CACHE_DURATION) {
      console.log(`[MyTickets] Using fresh cached data (${Math.round(age / 1000)}s old)`);
      return { tickets, isStale: false, age, contractAddress };
    }

    // Stale-while-revalidate - show old data while refreshing (up to 30 minutes)
    if (allowStale && age < STALE_WHILE_REVALIDATE) {
      console.log(`[MyTickets] Using stale cache while revalidating (${Math.round(age / 1000)}s old)`);
      return { tickets, isStale: true, age, contractAddress };
    }

    // Cache too old
    console.log('[MyTickets] Cache expired, will fetch fresh data');
    return null;
  } catch (error) {
    console.error('[MyTickets] Error reading cache:', error);
    return null;
  }
}

// Save tickets to localStorage cache
function setCachedTickets(account, tickets, contractAddress = null) {
  if (!account) return;

  try {
    const cacheKey = CACHE_KEY_PREFIX + account.toLowerCase();
    const cacheData = {
      tickets,
      timestamp: Date.now(),
      contractAddress, // Store contract address to validate cache
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    console.log(`[MyTickets] Cached ${tickets.length} tickets`);
  } catch (error) {
    console.error('[MyTickets] Error writing cache:', error);
  }
}

// Clear cache for account (called after new purchase)
export function clearTicketsCache(account) {
  if (!account) return;

  try {
    const cacheKey = CACHE_KEY_PREFIX + account.toLowerCase();
    localStorage.removeItem(cacheKey);
    console.log('[MyTickets] Cache cleared');
  } catch (error) {
    console.error('[MyTickets] Error clearing cache:', error);
  }
}

// Fetch ticket type details (name, price, etc.) from contract — with cache
const _ticketTypeCache = {};
async function getTicketTypeDetails(contract, ticketTypeId) {
  const key = String(ticketTypeId);
  if (_ticketTypeCache[key]) return _ticketTypeCache[key];

  try {
    const tt = await contract.getTicketType(Number(ticketTypeId));
    const details = {
      eventId: Number(tt[0]),
      name: tt[1] || '',
      price: tt[2]?.toString?.() || '0',
      maxSupply: Number(tt[3]),
      sold: Number(tt[4]),
      available: Number(tt[5]),
      saleStart: Number(tt[6]),
      saleEnd: Number(tt[7]),
      metadataURI: tt[8] || '',
    };
    _ticketTypeCache[key] = details;
    return details;
  } catch (err) {
    console.warn(`[MyTickets] Failed to get ticket type ${ticketTypeId}:`, err.message);
    return null;
  }
}

export default function MyTickets({ eventTicket, account }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cacheInfo, setCacheInfo] = useState({ using: false, isStale: false, age: 0 });
  const [lastRefresh, setLastRefresh] = useState(null);

  // Get contract address for cache validation
  const contractAddress = eventTicket?.target || eventTicket?.address || null;

  // ---------- Fetch from subgraph (fast, indexed) ----------
  const fetchTicketsFromSubgraph = async () => {
    try {
      const mints = await getTicketsByOwner(account, { first: 200 });
      if (!mints || mints.length === 0) return null;

      const contract = eventTicketWithEvents();
      const ticketsList = [];

      for (const mint of mints) {
        const ticketTypeId = Number(mint.ticketTypeId);
        const serialNumber = Number(mint.serialNumber);
        const tokenId = mint.tokenId;

        // Get ticket type details (name, price, etc.)
        const ttDetails = await getTicketTypeDetails(contract, ticketTypeId);

        // Check if ticket is used
        let used = false;
        try {
          used = await contract.isUsed(BigInt(tokenId));
        } catch {}

        ticketsList.push({
          id: tokenId.toString(),
          tokenId: tokenId.toString(),
          ticketTypeId,
          serialNumber,
          name: ttDetails?.name || '',
          price: ttDetails?.price || '0',
          metadataURI: ttDetails?.metadataURI || '',
          perks: [],
          used: Boolean(used),
          claimedPerks: [],
          checkinTime: 0,
          mintedAtBlock: Number(mint.blockNumber || 0),
          txHash: mint.txHash,
          eventName: mint.event?.name || '',
          eventId: ttDetails?.eventId || (mint.event?.eventId ? Number(mint.event.eventId) : 0),
        });
      }

      console.log(`[MyTickets] Found ${ticketsList.length} tickets from subgraph`);
      return ticketsList;
    } catch (err) {
      console.warn('[MyTickets] Subgraph unavailable:', err.message);
      return null;
    }
  };

  // ---------- Fallback: Fetch from contract (slow, scans all ticket types) ----------
  const fetchTicketsFromContract = async () => {
    try {
      const contract = eventTicketWithEvents();
      const ticketsFromBalance = [];

      // Get all event IDs → ticket type IDs → check balances
      let eventIds;
      try {
        eventIds = await contract.getAllEventIds();
      } catch {
        // Fallback: try scanning ticketTypeId 1-20
        eventIds = null;
      }

      let ticketTypeIds = [];
      if (eventIds && eventIds.length > 0) {
        // Get ticket types from each event
        for (const eid of eventIds) {
          try {
            const ttIds = await contract.getEventTicketTypes(Number(eid));
            for (const ttId of ttIds) {
              ticketTypeIds.push(Number(ttId));
            }
          } catch {}
        }
      }

      // Fallback: scan common ticket type IDs
      if (ticketTypeIds.length === 0) {
        ticketTypeIds = Array.from({ length: 20 }, (_, i) => i + 1);
      }

      // Deduplicate
      ticketTypeIds = [...new Set(ticketTypeIds)];

      for (const ttId of ticketTypeIds) {
        // Get ticket type details first (to know max serial numbers)
        const ttDetails = await getTicketTypeDetails(contract, ttId);
        const maxSerial = ttDetails ? ttDetails.sold : 10; // Check up to sold count
        if (maxSerial === 0) continue;

        for (let serial = 1; serial <= Math.min(maxSerial, 50); serial++) {
          try {
            // eslint-disable-next-line no-undef
            const tokenId = (BigInt(ttId) << 128n) | BigInt(serial);
            const balance = await contract.balanceOf(account, tokenId);

            if (balance > 0n) {
              console.log(`[MyTickets] Found token ${tokenId}: balance ${balance}`);

              let used = false;
              try { used = await contract.isUsed(tokenId); } catch {}

              ticketsFromBalance.push({
                id: tokenId.toString(),
                tokenId: tokenId.toString(),
                ticketTypeId: ttId,
                serialNumber: serial,
                name: ttDetails?.name || '',
                price: ttDetails?.price || '0',
                metadataURI: ttDetails?.metadataURI || '',
                perks: [],
                used: Boolean(used),
                claimedPerks: [],
                checkinTime: 0,
                mintedAtBlock: 0,
                eventId: ttDetails?.eventId || 0,
              });
            }
          } catch {
            // Skip this token ID if balance check fails
          }
        }
      }

      console.log(`[MyTickets] Found ${ticketsFromBalance.length} tickets from contract scan`);
      return ticketsFromBalance;
    } catch (error) {
      console.error('[MyTickets] Error scanning contract:', error);
      return [];
    }
  };

  // ---------- Combined fetch ----------
  const fetchTickets = useCallback(async (showRefreshIndicator = false) => {
    if (!account) return [];

    if (showRefreshIndicator) {
      setRefreshing(true);
    }

    try {
      // 1. Try subgraph first (fast)
      const sgTickets = await fetchTicketsFromSubgraph();
      if (sgTickets && sgTickets.length > 0) {
        if (showRefreshIndicator) setRefreshing(false);
        return sgTickets;
      }

      // 2. Fallback to contract scan (slow)
      const contractTickets = await fetchTicketsFromContract();
      if (showRefreshIndicator) setRefreshing(false);
      return contractTickets;
    } catch (error) {
      console.error('[MyTickets] Error loading tickets:', error);
      if (showRefreshIndicator) setRefreshing(false);
      return [];
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  // Manual refresh handler
  const handleRefresh = useCallback(async () => {
    if (refreshing) return;

    setRefreshing(true);
    const freshTickets = await fetchTickets(false);
    setTickets(freshTickets);
    setCachedTickets(account, freshTickets, contractAddress);
    setCacheInfo({ using: false, isStale: false, age: 0 });
    setLastRefresh(Date.now());
    setRefreshing(false);
  }, [refreshing, fetchTickets, account, contractAddress]);

  useEffect(() => {
    const loadTickets = async () => {
      if (!account) {
        setTickets([]);
        setLoading(false);
        return;
      }

      // Try to get cached tickets first (allow stale data)
      const cached = getCachedTickets(account, true);

      // Validate cache is for the same contract
      if (cached && cached.contractAddress && cached.contractAddress !== contractAddress) {
        console.log('[MyTickets] Cache invalidated - contract address changed');
        clearTicketsCache(account);
      } else if (cached && cached.tickets.length > 0) {
        // Show cached data immediately
        setTickets(cached.tickets);
        setCacheInfo({ using: true, isStale: cached.isStale, age: cached.age });
        setLoading(false);

        // Sync XP for cached tickets (in case it wasn't synced before)
        const syncResult = syncXPFromTickets(account, cached.tickets.length);
        if (syncResult) {
          console.log(`[MyTickets] XP synced from cache: +${syncResult.xpAwarded} XP for ${syncResult.ticketCount} tickets`);
        }

        // If cache is stale, refresh in background
        if (cached.isStale) {
          console.log('[MyTickets] Background refresh started...');
          const freshTickets = await fetchTickets(true);
          if (freshTickets.length > 0) {
            setTickets(freshTickets);
            setCachedTickets(account, freshTickets, contractAddress);
            setCacheInfo({ using: false, isStale: false, age: 0 });
            setLastRefresh(Date.now());
          }
        }
        return;
      }

      // No cache or empty cache - fetch from blockchain
      const freshTickets = await fetchTickets(false);
      setTickets(freshTickets);
      setCachedTickets(account, freshTickets, contractAddress);
      setCacheInfo({ using: false, isStale: false, age: 0 });
      setLastRefresh(Date.now());
      setLoading(false);

      // Sync XP for existing tickets (retroactive award)
      if (freshTickets.length > 0) {
        const syncResult = syncXPFromTickets(account, freshTickets.length);
        if (syncResult) {
          console.log(`[MyTickets] XP synced: +${syncResult.xpAwarded} XP for ${syncResult.ticketCount} tickets`);
        }
      }
    };

    loadTickets();
  }, [account, contractAddress, fetchTickets]);

  // Format cache age for display
  const formatCacheAge = (ageMs) => {
    const seconds = Math.floor(ageMs / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h`;
  };


  if (loading) {
    return (
      <div className="my-tickets">
        <div className="loading-container">
          <div className="tickets-loader"></div>
          <p className="loading-text">Loading your tickets...</p>
        </div>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="my-tickets">
        <div className="empty-state">
          <p>No tickets found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-tickets">
      <div className="tickets-header">
        <h2>Your Tickets ({tickets.length})</h2>
        <div className="tickets-header-actions">
          {cacheInfo.using && (
            <span
              className={`cache-indicator ${cacheInfo.isStale ? 'stale' : 'fresh'}`}
              title={cacheInfo.isStale ? 'Refreshing in background...' : `Cached ${formatCacheAge(cacheInfo.age)} ago`}
            >
              {cacheInfo.isStale ? 'Updating...' : `${formatCacheAge(cacheInfo.age)}`}
            </span>
          )}
          <button
            className={`refresh-button ${refreshing ? 'refreshing' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing || loading}
            title={lastRefresh ? `Last refreshed: ${new Date(lastRefresh).toLocaleTimeString()}` : 'Refresh tickets'}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>
      <div className="tickets-scroll-container">
        <div className="tickets-table">
          {tickets.map((ticket) => (
            <TicketItem
              key={ticket.id}
              ticket={ticket}
              eventTicket={eventTicket}
              account={account}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
